import React, { useContext } from 'react';
import {
  IBusinessColorConfig,
  IBusinessConfigs,
} from '../_Common/Const/BusinessConfigs';
import { initConst } from '../../../Utils/utils';
import { TEDGAGravUPL_STATUSEnum } from '../../../globalQueryTypes';
import EDIconButton from '../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { DataTableStatusDisplay } from './ObjektVyer/Components/DataTableStatusDisplay';
import { useTheme } from '@mui/material';
import { ValueType } from '../../../Utils/Formatters/FormatterTypes';
import BusinessDetailContext from '../_Common/Components/BusinessDetailContext/BusinessDetailContext';

interface IUpplatelseStatusProps {
  value: ValueType;
  displayValue: string;
}

const createGraveStatusColorStyles = (
  dataPrefix: string = ''
): IBusinessColorConfig[] => {
  const prefix = dataPrefix ? `${dataPrefix}.` : '';

  return [
    {
      caption: 'Finns ej mer',
      color: 'red',
      colorOpacity: 'faded',
      condition: (data: any) =>
        data[`${prefix}UPL_STATUS`] === TEDGAGravUPL_STATUSEnum.FINNS_EJ_MER,
      dataDependencies: [`${prefix}UPL_STATUS`],
    },
    {
      caption: 'Kan ej upplåtas',
      color: 'red',
      colorOpacity: 'faded',
      condition: (data: any) =>
        data[`${prefix}UPL_STATUS`] === TEDGAGravUPL_STATUSEnum.KAN_EJ_UPPLATAS,
      dataDependencies: [`${prefix}UPL_STATUS`],
    },
    {
      caption: 'Ej upplåten med framtida gravrätt',
      color: 'orange',
      colorOpacity: 'faded',
      condition: (data: any) => {
        const isEjUpplaten =
          data[`${prefix}UPL_STATUS`] === TEDGAGravUPL_STATUSEnum.EJ_UPPLATEN;
        const gravratt = data[`${prefix}Gravratt`];
        const hasFutureDate = gravratt && new Date(gravratt) > new Date();
        return isEjUpplaten && hasFutureDate;
      },
      dataDependencies: [`${prefix}UPL_STATUS`, `${prefix}Gravratt`],
    },
    {
      caption: 'Ej upplåten',
      color: 'lightGreen',
      colorOpacity: 'faded',
      condition: (data: any) => {
        const isEjUpplaten =
          data[`${prefix}UPL_STATUS`] === TEDGAGravUPL_STATUSEnum.EJ_UPPLATEN;
        const gravratt = data[`${prefix}Gravratt`];
        const hasFutureDate = gravratt && new Date(gravratt) > new Date();
        return isEjUpplaten && !hasFutureDate;
      },
      dataDependencies: [`${prefix}UPL_STATUS`, `${prefix}Gravratt`],
    },
    {
      caption: 'Upplåten',
      color: 'gray',
      colorOpacity: 'faded',
      condition: (data: any) =>
        data[`${prefix}UPL_STATUS`] === TEDGAGravUPL_STATUSEnum.UPPLATEN,
      dataDependencies: [`${prefix}UPL_STATUS`],
    },
  ];
};

const UpplatelseStatusDisplay: React.FC<IUpplatelseStatusProps> = props => {
  const theme = useTheme();
  const businessDetailContext = useContext(BusinessDetailContext);
  const valueString = props?.value?.toLocaleString() ?? '';
  const colorStyles = createGraveStatusColorStyles();

  const matchingStyle = colorStyles.find(style =>
    style.condition({
      UPL_STATUS: valueString,
      Gravratt: businessDetailContext?.data?.Gravratt,
    })
  );

  if (!matchingStyle) {
    return (
      <DataTableStatusDisplay
        value={props.value}
        displayValue={props.displayValue}
        backgroundColor="transparent"
        fontColor={theme.palette.text.primary}
        variant="filled"
      />
    );
  }

  const backgroundColor =
    theme.customProperties.Legend[matchingStyle.color!][
      matchingStyle.colorOpacity!
    ];

  return (
    <DataTableStatusDisplay
      value={props.value}
      displayValue={props.displayValue}
      caption={matchingStyle.caption}
      backgroundColor={backgroundColor}
      fontColor={'black'}
      variant="filled"
    />
  );
};

const GABusinessConfig = initConst<IBusinessConfigs>(() => ({
  GAGrav: {
    defaultColumns: [
      { attributeDef: 'GravNr', caption: 'Gravplatsnummer', header: true },
      { attributeDef: 'GravTyp', caption: 'Typ' },
      { attributeDef: 'UPL_STATUS', caption: 'Upplåtelsestatus' },
      { attributeDef: 'GRAVRATT_FR', caption: 'Gravr. fr o m' },
      { attributeDef: 'GRAVRATT_TI', caption: 't o m' },
      { attributeDef: 'KANNETECKEN', caption: 'Kännetecken' },
      { attributeDef: 'GravrInhNamnTyp', caption: 'Senaste Gravr.inh.' },
      {
        attributeDef: 'TidigareUplOmg',
        caption: 'Antal tidigare upplåtelseomgångar',
      },
      { attributeDef: 'ANDRINGSDATUM', caption: 'Senast ändrad' },
    ],
    attributes: {
      UPL_STATUS: {
        displayComp: UpplatelseStatusDisplay,
        hideTooltip: true,
        tableMinWidth: 160,
      },
    },
    listIcons: [
      {
        componentFn: config =>
          config.data?.VanvardMarkering?.includes('VANVARD') ? (
            <EDIconButton
              key="vanvard-icon"
              icon="warning"
              tooltip={`Vanvård: ${config.data.VanvardMarkering}`}
              disabled={config.disabled}
              iconSize="medium"
              margin="none"
              padding="dense"
              color="secondary"
            />
          ) : null,
        dataDependencies: ['VanvardMarkering'],
      },
      {
        componentFn: config => {
          if (config.data?.VanvardMarkering?.includes('VANVARD')) {
            return null;
          }
          // FIXME? finns det ett mindre "kostsamt" sätt än att hämta alla skötslar på varje grav?
          const giltigaSkotslar =
            config.data?._Associations?.GiltigaSkotslar_All;
          const hasActiveSkotsel =
            giltigaSkotslar && giltigaSkotslar.length > 0;
          return (
            <EDIconButton
              key="skotsel-icon"
              icon="cloud_upload"
              tooltip={
                hasActiveSkotsel
                  ? `Aktiv skötsel: ${
                      giltigaSkotslar[0]?.BenamningShort || 'Skötsel finns'
                    }`
                  : 'Ingen aktiv skötsel'
              }
              disabled={config.disabled}
              iconSize="medium"
              margin="none"
              padding="dense"
              color={hasActiveSkotsel ? 'primary' : 'inherit'}
            />
          );
        },
        dataDependencies: ['GiltigaSkotslar.id', 'VanvardMarkering'],
      },
      {
        componentFn: config => {
          // FIXME? finns det ett mindre "kostsamt" sätt än att hämta alla kontakter på varje grav?
          const kontakter = config.data?._Associations?.Kontakter_All;
          const innehavare = kontakter?.filter(
            (k: { _typ: string }) => k._typ === 'GRAVRATTSINNEHAVARE'
          );

          if (!innehavare || innehavare.length === 0) return null;

          const hasMultiple = innehavare.length > 1;
          const names = innehavare
            .map((i: { IDString: any }) => i.IDString)
            .join(', ');

          return (
            <EDIconButton
              key="gravrattsinnehavare-icon"
              icon={hasMultiple ? 'people' : 'person'}
              tooltip={`Gravrättsinnehavare: ${names}`}
              disabled={config.disabled}
              iconSize="medium"
              margin="none"
              padding="dense"
              color="inherit"
            />
          );
        },
        dataDependencies: ['Kontakter.IDString', 'Kontakter._typ'],
      },
      {
        componentFn: config => {
          const gravsatta = config.data?._Associations?.Gravsatta_All;

          if (!gravsatta || gravsatta.length === 0) return null;

          const hasKista = gravsatta.some(
            (g: { _Associations: { Avlidna: { GRAVTYP: string } } }) =>
              g._Associations?.Avlidna?.GRAVTYP === 'KISTA'
          );
          const hasUrn = gravsatta.some(
            (g: { _Associations: { Avlidna: { GRAVTYP: string } } }) =>
              g._Associations?.Avlidna?.GRAVTYP === 'URNA'
          );

          let icon: 'bar_chart' | 'coffin' | 'cremation_plain' | null;

          if (hasKista && hasUrn) {
            icon = 'bar_chart';
          } else if (hasKista) {
            icon = 'coffin';
          } else if (hasUrn) {
            icon = 'cremation_plain';
          } else {
            icon = null;
          }

          if (!icon) return null;

          const tooltip =
            hasKista && hasUrn
              ? 'Urnor/aska i kistmall'
              : hasKista
              ? 'Kistor'
              : 'Urnor/aska';

          return (
            <EDIconButton
              key="burial-type-icon"
              icon={icon}
              tooltip={tooltip}
              disabled={config.disabled}
              iconSize="medium"
              margin="none"
              padding="dense"
              color="inherit"
            />
          );
        },
        dataDependencies: ['Gravsatta.Avlidna.GRAVTYP'],
      },
    ],
    colors: {
      hideLegendColumn: true,
      styles: createGraveStatusColorStyles(),
    },
    unusedBusinessMenuButtons: ['add'],
  },
  GAGravsatt: {
    defaultColumns: [
      { attributeDef: 'Namn', caption: 'Namn' },
      { attributeDef: 'Avliden.OrgNrPersNr', caption: 'Personnummer' },
      { attributeDef: 'Begravningsplats.GravNr', caption: 'Gravplats' },
      { attributeDef: 'GRAVS_DATUM', caption: 'Gravs.datum' },
      { attributeDef: 'DODSDATUM', caption: 'Dödsdatum' },
      { attributeDef: 'BEGR_DATUM', caption: 'Begr.datum' },
    ],
  },
  GAKontakt: {
    defaultColumns: [
      { attributeDef: 'OrgPers2.Namn', caption: 'Namn' },
      { attributeDef: 'OrgPers2.OrgNrPersNr', caption: 'Org.nr/Pers.nr' },
      { attributeDef: 'KontaktTyp', caption: 'Typ' },
      { attributeDef: 'Grav.GravNr', caption: 'Gravplats' },
      { attributeDef: 'OrgPers2.Adress.AdressTelefonRad', caption: 'Adress' },
    ],
  },
  GASkotsel: {
    defaultColumns: [
      { attributeDef: 'Grav.GravNr', caption: 'Gravplats' },
      { attributeDef: 'Ar', caption: 'År' },
      { attributeDef: 'MANAD', caption: 'Mån.' },
      { attributeDef: 'Artikel.ARTIKELNR', caption: 'Artikel' },
      { attributeDef: 'BenamningShort', caption: 'Benämning' },
      { attributeDef: 'DebsattPaket', caption: 'Debiteringssätt' },
      { attributeDef: 'AntalSQL', caption: 'Antal' },
      { attributeDef: 'Artikel.Enhet.ENHET', caption: 'Enhet' },
      { attributeDef: 'PrisSQL', caption: 'Pris' },
      { attributeDef: 'BeloppSQL', caption: 'Belopp' },
      { attributeDef: 'DebiteradTom', caption: 'Debiterad t.o.m.' },
    ],
  },
  GAHandelse: {
    defaultColumns: [
      { attributeDef: 'TYP', caption: 'Typ' },
      { attributeDef: 'RUBRIK', caption: 'Rubrik' },
      { attributeDef: 'DATUM', caption: 'Datum' },
      { attributeDef: 'SIGNATUR', caption: 'Signatur' },
    ],
  },
  GAMyCartaUrval: {
    defaultColumns: [
      { attributeDef: 'NAMN', caption: 'Namn' },
      { attributeDef: 'SQLDATE', caption: 'Urval senast ändrad' },
    ],
  },
  GAKartaTema: {
    defaultColumns: [
      { attributeDef: 'NAMN', caption: 'Namn' },
      { attributeDef: 'SQLDATE', caption: '' },
    ],
  },
  GAArbUnd: {
    defaultColumns: [
      { attributeDef: 'NUMMER', caption: 'Nr' },
      { attributeDef: 'RUBRIK', caption: 'Rubrik' },
      { attributeDef: 'Skapad', caption: 'Skapat' },
      { attributeDef: 'Utskriven', caption: 'Utskrivet' },
      { attributeDef: 'Backad', caption: 'Backat' },
    ],
  },
  GASkotselKategori: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: '' },
      { attributeDef: 'AntalArtiklar', caption: '' },
      { attributeDef: 'AntalGravar', caption: '' },
    ],
  },
  GASkotselBest: {
    defaultColumns: [
      { attributeDef: 'BESTALLNINGSTID', caption: '' },
      { attributeDef: 'NAMN', caption: '' },
      { attributeDef: 'ORDERNUMMER', caption: '' },
      { attributeDef: 'STATUS', caption: 'Status' },
      {
        attributeDef: 'Grav.GravrInhOmbud.OrgPers2.NamnFE',
        caption: 'Gravrättsinnehavare',
      },
      { attributeDef: 'EPOST', caption: '' },
    ],
  },
  GAOrderOmg: {
    defaultColumns: [
      { attributeDef: 'ORDER_OMG', caption: '' },
      { attributeDef: 'RUBRIK', caption: '' },
      { attributeDef: 'StatusBen', caption: '' },
      { attributeDef: 'DATUM_SKAPAD', caption: '' },
      { attributeDef: 'SIGN_SKAPAD', caption: '' },
      { attributeDef: 'DATUM_BACKAD', caption: '' },
      { attributeDef: 'SIGN_BACKAD', caption: '' },
    ],
  },
  GAInfokiosk: {
    defaultColumns: [
      { attributeDef: 'DATUM', caption: 'Datum' },
      { attributeDef: 'PUBLICERAS_FRAN', caption: 'Publiceras från' },
      { attributeDef: 'PUBLICERAS_TILL', caption: 'Publiceras till' },
      { attributeDef: 'RUBRIK', caption: 'Rubrik' },
      { attributeDef: 'TEXT', caption: 'Text' },
    ],
  },
  GAGravList: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: 'Benämning' },
      { attributeDef: 'TYP', caption: 'Typ' },
    ],
  },
}));

export default GABusinessConfig;
